import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from "@angular/router";
import { TenantService } from "./tenant.service";
import { TokenStorageService } from "./token-storage.service";
import { EnvironmentLoaderService } from "src/ancestors/env-config.service";
import { UrlCollection } from "../app-routing.module";
import { clearClientSession } from "./session-cleanup";


/**
 * Guard that handles tenant switching completion when the application loads with a switchToken.
 * 
 * This guard:
 * 1. Checks if there's a switchToken in the query parameters
 * 2. If found, completes the tenant switch process
 * 3. Removes the token from the URL and allows navigation
 * 
 * @param route - The activated route snapshot
 * @param state - The router state snapshot
 * @returns Promise<boolean> - true if navigation should proceed, false otherwise
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function TenantSwitchGuardFn(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
  const tenantService = inject(TenantService);
  const tokenStorage = inject(TokenStorageService);
  const router = inject(Router);
  const envService = inject(EnvironmentLoaderService);
  // Store the temporary token for the completion process
  const env = envService.getEnvConfig();
  const tokenKey = env.loginType === "local" ? env.localTokenKey : env.ssoTokenKey;

  // Use window.location to get query parameters (same approach as AuthenticationGuard)
  const actualUrl = new URL(window.location.href.replace("/#/", "/"));
  const switchToken = actualUrl.searchParams.get("switchToken");

  // Se non c'è uno switchToken non c'è nulla da completare: si prosegue, e sarà
  // AuthenticationGuardFn a decidere se la sessione esistente è valida.
  //
  // NOTA: prima qui si usciva appena esisteva un token, PRIMA di guardare lo switchToken. Con
  // anagrafica e ruoli per tenant quel cortocircuito è un difetto: lo switchToken è il solo
  // portatore dell'identità del tenant di DESTINAZIONE, e ignorarlo lascia in piedi la sessione
  // del tenant di provenienza — l'utente crede di aver cambiato tenant e continua invece a
  // vedere nome, ruoli e dati del precedente. Succede tutte le volte che i due tenant
  // condividono l'origine (in sviluppo, tenant distinti solo per porta o sottodominio).
  if (!switchToken) {
    return true;
  }

  try {
    // leggi il sessionId dal query params
    const sessionId = actualUrl.searchParams.get("sessionId");
    if (!sessionId) {
      throw new Error("Session ID not found");
    }

    // La sessione del tenant di PROVENIENZA va rimossa prima di crearne una nuova: se i due
    // tenant condividono l'origine, il token vecchio sovrascriverebbe quello nuovo o resterebbe
    // in uso per le richieste che partono prima del salvataggio.
    // `localStorage` è per ORIGINE, non per tenant: quando due tenant condividono l'origine (in
    // sviluppo si distinguono spesso solo per porta) l'area selezionata e la lingua del tenant da
    // cui si arriva sopravvivono allo switch. L'area, in particolare, appartiene al database del
    // tenant precedente: riusarla significa filtrare i dati del tenant nuovo con l'id di un'area
    // che lì non esiste.
    clearClientSession(tokenStorage, tokenKey);

    // Complete the tenant switch process
    await tenantService.completeTenantSwitch(switchToken, sessionId);

    // Remove the switchToken from the URL
    await router.navigate([UrlCollection.homepage], { replaceUrl: true });

    return true;
  } catch (error) {
    // Log error and redirect to login on error
    clearClientSession(tokenStorage, tokenKey);
    await router.navigate(["/login"]);
    return false;
  }
}
